import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, RotateCcw } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import DeviceCard from "@/components/DeviceCard";
import { fetchDevices } from "@/lib/api";
import { GAMES, CHIPSET_TIERS, CONDITIONS, FPS_TARGETS, COOLING, PRICE_FILTER_BANDS, SORTS } from "@/data/static";

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export default function FpsFinder() {
  const [params, setParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [fps, setFps] = useState(null);
  const [game, setGame] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cooling, setCooling] = useState([]);
  const [band, setBand] = useState(null);
  const [pta, setPta] = useState(null);
  const [sort, setSort] = useState("popularity");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDevices().then(setAll).catch(() => {});
  }, []);

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  const brandOptions = useMemo(() => [...new Set(all.map((d) => d.brand))].sort(), [all]);

  const results = useMemo(() => {
    let list = all.filter((d) => {
      if (q) {
        const hay = `${d.name} ${d.chipset} ${d.brand}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      if (fps && d.fps_tier < fps) return false;
      if (game && !(d.games && d.games[game])) return false;
      if (tiers.length && !tiers.includes(d.chipset_tier)) return false;
      if (conditions.length && !conditions.some((c) => d.conditions.includes(c))) return false;
      if (brands.length && !brands.includes(d.brand)) return false;
      if (cooling.length && !cooling.includes(d.cooling_rating)) return false;
      if (band && !(d.price_min <= band.max && d.price_max >= band.min)) return false;
      if (pta) {
        const s = d.pta_status.toLowerCase();
        if (pta === "PTA" && !(s.includes("approved") || s.includes("usually pta"))) return false;
        if (pta === "Non-PTA" && !(s.includes("non-pta") || s.includes("kit"))) return false;
      }
      return true;
    });
    const cmp = {
      price_asc: (a, b) => a.price_min - b.price_min,
      gaming: (a, b) => b.fps_tier - a.fps_tier,
      popularity: (a, b) => b.popularity - a.popularity,
      latest: (a, b) => (b.updated_date || "").localeCompare(a.updated_date || ""),
    }[sort];
    return [...list].sort(cmp);
  }, [all, q, fps, game, tiers, conditions, brands, cooling, band, pta, sort]);

  const reset = () => {
    setFps(null); setGame(null); setTiers([]); setConditions([]); setBrands([]); setCooling([]); setBand(null); setPta(null); setQ(""); setParams({});
  };

  const activeCount = [fps, game, band, pta].filter(Boolean).length + tiers.length + conditions.length + brands.length + cooling.length;

  const FilterPanel = (
    <div className="space-y-6">
      <FilterGroup title="FPS Target">
        <div className="flex flex-wrap gap-2">
          {FPS_TARGETS.map((f) => (
            <Chip key={f} testid={`filter-fps-${f}`} active={fps === f} onClick={() => setFps(fps === f ? null : f)}>{f} FPS</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Game">
        <div className="flex flex-wrap gap-2">
          {GAMES.map((g) => (
            <Chip key={g.key} testid={`filter-game-${g.key}`} active={game === g.key} onClick={() => setGame(game === g.key ? null : g.key)}>{g.label}</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Budget (PKR)">
        <div className="flex flex-wrap gap-2">
          {PRICE_FILTER_BANDS.map((b) => (
            <Chip key={b.label} testid={`filter-band-${b.label}`} active={band?.label === b.label} onClick={() => setBand(band?.label === b.label ? null : b)}>{b.label}</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Chipset Tier">
        <div className="flex flex-wrap gap-2">
          {CHIPSET_TIERS.map((t) => (
            <Chip key={t} testid={`filter-tier-${t}`} active={tiers.includes(t)} onClick={() => setTiers(toggle(tiers, t))}>{t}</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Condition">
        <div className="flex flex-wrap gap-2">
          {CONDITIONS.map((c) => (
            <Chip key={c} testid={`filter-condition-${c}`} active={conditions.includes(c)} onClick={() => setConditions(toggle(conditions, c))}>{c}</Chip>
          ))}
        </div>
        {conditions.includes("CPID/Patched") && (
          <p className="mt-2 text-[11px] leading-relaxed text-amberw">
            ⚠ CPID/Patched status is informational only, can change with updates, and is not legal advice.
          </p>
        )}
      </FilterGroup>

      <FilterGroup title="PTA Status">
        <div className="flex flex-wrap gap-2">
          {["PTA", "Non-PTA"].map((p) => (
            <Chip key={p} testid={`filter-pta-${p}`} active={pta === p} onClick={() => setPta(pta === p ? null : p)}>{p}</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Cooling">
        <div className="flex flex-wrap gap-2">
          {COOLING.map((c) => (
            <Chip key={c} testid={`filter-cooling-${c}`} active={cooling.includes(c)} onClick={() => setCooling(toggle(cooling, c))}>{c}</Chip>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title="Brand">
        <div className="flex flex-wrap gap-2">
          {brandOptions.map((b) => (
            <Chip key={b} testid={`filter-brand-${b}`} active={brands.includes(b)} onClick={() => setBrands(toggle(brands, b))}>{b}</Chip>
          ))}
        </div>
      </FilterGroup>

      <button data-testid="reset-filters" onClick={reset} className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-graphite py-2.5 text-sm font-medium text-slate-300 hover:border-dangerr/50 hover:text-dangerr">
        <RotateCcw className="h-4 w-4" /> Reset filters
      </button>
    </div>
  );

  return (
    <>
      <Seo title="FPS Finder - Filter Gaming Phones" description="Filter and compare gaming phones in Pakistan by budget, FPS target, game, chipset, condition and PTA status." path="/fps-finder" />

      <div className="border-b border-border bg-graphite">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="font-display text-2xl font-black tracking-tight text-white sm:text-3xl">FPS Finder</h1>
          <p className="mt-1 text-sm text-slate-400">Filter Pakistan's gaming phone database by budget, FPS, game & condition.</p>
          <form onSubmit={(e) => { e.preventDefault(); setParams(q ? { q } : {}); }} className="mt-5 max-w-xl">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-slateSurface px-3 py-2.5 focus-within:border-neon/50">
              <Search className="h-4 w-4 text-slate-500" />
              <input data-testid="finder-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search phone or chipset…" className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none" />
            </div>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 rounded-xl border border-border bg-slateSurface p-5">
              <div className="mb-4 flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-neon" />
                <h2 className="font-heading text-sm font-bold text-white">Filters</h2>
              </div>
              {FilterPanel}
            </div>
          </aside>

          <div>
            <div className="mb-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button data-testid="mobile-filter-toggle" onClick={() => setShowFilters(true)} className="flex items-center gap-2 rounded-lg border border-border bg-slateSurface px-3 py-2 text-sm font-medium text-white lg:hidden">
                  <SlidersHorizontal className="h-4 w-4 text-neon" /> Filters {activeCount > 0 && <span className="rounded-full bg-neon px-1.5 text-xs font-bold text-charcoal">{activeCount}</span>}
                </button>
                <span data-testid="results-count" className="text-sm text-slate-400">{results.length} phones</span>
              </div>
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-border bg-slateSurface px-3 py-2 text-sm text-white focus:border-cyber/50 focus:outline-none">
                {SORTS.map((s) => <option key={s.key} value={s.key} className="bg-slateSurface">{s.label}</option>)}
              </select>
            </div>

            {results.length === 0 ? (
              <div data-testid="no-results" className="rounded-xl border border-border bg-slateSurface p-10 text-center text-slate-400">
                No phones match your filters. Try resetting.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {results.map((d) => <DeviceCard key={d.slug} device={d} />)}
              </div>
            )}

            <div className="mt-8"><Disclaimer /></div>
          </div>
        </div>
      </div>

      {/* mobile filter sheet */}
      {showFilters && (
        <div className="fixed inset-0 z-[55] flex lg:hidden">
          <div className="flex-1 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="w-80 max-w-[85%] overflow-y-auto border-l border-border bg-slateSurface p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-white">Filters</h2>
              <button data-testid="close-filter-sheet" onClick={() => setShowFilters(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            {FilterPanel}
            <button onClick={() => setShowFilters(false)} className="mt-4 w-full rounded-lg bg-neon py-2.5 text-sm font-heading font-bold text-charcoal">Show {results.length} phones</button>
          </div>
        </div>
      )}
    </>
  );
}

const FilterGroup = ({ title, children }) => (
  <div>
    <h3 className="mb-2 text-xs font-heading font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
    {children}
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${
      active ? "border-neon bg-neon/15 text-neon" : "border-border bg-graphite text-slate-300 hover:border-slate-500"
    }`}
  >
    {children}
  </button>
);
